import { Stack } from '@mui/material'
import { useCallback } from 'react'
import { ScrollTag } from 'utoniq-ui'

import { Maybe, PostBoxFragment, PostType } from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, SideMenuProps } from '@/components/layouts'
import { PostBox, PostBoxSkeleton } from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Template } from '@/core/architecture'

type FilterType = PostType | 'general'

export type State = {
  posts: PostBoxFragment[]
  filter: FilterType
  loading: boolean
  refetching: boolean
  artistName: string
  hasNextPage: boolean
  liveStreamId: string | undefined
  user?: Maybe<SideMenuProps['user']>
}

export type Action = {
  onFilter: (filterType: FilterType) => void
  onFetchMore: () => Promise<void>
  onLikePost: (
    postId: string,
    isLike: boolean,
    likeCount: number
  ) => Promise<void>
}

export const ArtistPostsTemplate: Template<State, Action> = ({
  state: {
    filter,
    posts,
    loading,
    refetching,
    hasNextPage,
    artistName,
    liveStreamId,
    user,
  },
  onFilter,
  onFetchMore,
  onLikePost,
}) => {
  const { t } = useTranslation('artistPosts')
  const { router, routerPages, routerNames, generatePath } = useRouter<{
    artistId: string
  }>()

  return (
    <ArtistThemeProvider>
      <BaseLayout
        navigation={
          config.artistTabs.includes('posts')
            ? {
                type: 'artist',
                props: {
                  hasManagementTask: !!user?.hasManagementTask,
                  unreadNotificationCount: user?.unreadNotificationCount,
                  isLive: !!liveStreamId,
                },
              }
            : undefined
        }
        margin
        header={{
          title: t('title'),
          back: true,
          defaultBackPage: generatePath(routerPages.ArtistTop, {
            artistId:
              (config.applicationType === 'standalone'
                ? config.artistId
                : router.query.artistId) ?? '#',
          }),
        }}
      >
        <Head title={[routerNames.ArtistPosts, artistName]} />
        <FilterMenu filter={filter} onFilter={onFilter} />

        <ScrollFetcher
          componentProps={{ sx: { pt: 2 }, spacing: 5 }}
          nodes={posts.map((post, index) => (
            <PostBox
              onLike={async () => {
                await onLikePost(post.id, post.isLike, post.likeCount)
              }}
              isMembershipPersonalInformationRequired={
                !!user?.isMembershipPersonalInformationRequired
              }
              sx={{ height: 356 }}
              key={index}
              post={post}
              fullWidth
            />
          ))}
          onFetchMore={onFetchMore}
          loading={refetching || loading}
          hasNextPage={hasNextPage}
          emptyMessage={t('postNotExist')}
          loadingComponent={
            <Stack spacing={3} sx={{ pt: 2 }}>
              {Array.from(Array(3).keys()).map(key => (
                <PostBoxSkeleton fullWidth key={key} />
              ))}
            </Stack>
          }
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

const FilterMenu = ({
  filter,
  onFilter,
}: {
  filter: FilterType
  onFilter: (filterType: FilterType) => void
}) => {
  const { t } = useTranslation('artistPosts')

  const handleSelect = useCallback(
    (value: string) => {
      onFilter(value as FilterType)
    },
    [onFilter]
  )

  return (
    <ScrollTag
      value={filter}
      onClick={handleSelect}
      tags={[
        { value: 'general', label: t('general') },
        { value: 'video', label: t('video') },
        { value: 'sound', label: t('sound') },
        { value: 'image', label: t('image') },
        { value: 'text', label: t('text') },
      ]}
    />
  )
}
